import React, { useState } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Typography, IconButton
} from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'

const fmt = (v) => {
  if (!v || v === 0) return '—'
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const headerStyle = {
  fontWeight: 700,
  fontSize: '0.8rem',
  backgroundColor: 'var(--df-primary, #1f5e3a)',
  color: '#fbf8f1',
  padding: '8px 12px'
}

const catHeaderStyle = {
  fontWeight: 700,
  fontSize: '0.85rem',
  backgroundColor: 'var(--df-primary-2, #164a2c)',
  color: '#fbf8f1',
  padding: '8px 12px'
}

const subtotalStyle = {
  fontWeight: 700,
  fontSize: '0.8rem',
  backgroundColor: '#eceff1',
  padding: '6px 12px'
}

const COST_TYPE_COLORS = {
  labor: '#2e7d32',
  equipment: '#e65100',
  material: '#283593',
  subcontract: '#1565c0',
  disposal: '#c62828'
}

const ServiceOutputTable = ({ data }) => {
  const [expanded, setExpanded] = useState({})

  if (!data || !data.categories) {
    return <Typography variant="body2" color="textSecondary" style={{ padding: 20 }}>No service data available</Typography>
  }

  const toggleExpand = (svcId) => {
    setExpanded(prev => ({ ...prev, [svcId]: !prev[svcId] }))
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table size="small" style={{ borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...headerStyle, width: 30 }} />
            <TableCell style={headerStyle}>Service</TableCell>
            <TableCell align="right" style={headerStyle}>Qty</TableCell>
            <TableCell style={headerStyle}>Unit</TableCell>
            <TableCell align="right" style={headerStyle}>Labor</TableCell>
            <TableCell align="right" style={headerStyle}>Material</TableCell>
            <TableCell align="right" style={headerStyle}>Equipment</TableCell>
            <TableCell align="right" style={headerStyle}>Sub/Disp</TableCell>
            <TableCell align="right" style={headerStyle}>Total Cost</TableCell>
            <TableCell align="right" style={headerStyle}>Total Sell</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.categories.map((cat) => (
            <React.Fragment key={cat.category}>
              {/* Category header */}
              <TableRow>
                <TableCell colSpan={10} style={catHeaderStyle}>
                  {cat.category}
                </TableCell>
              </TableRow>

              {/* Service rows */}
              {cat.services.map((svc) => (
                <React.Fragment key={svc._id}>
                  <TableRow hover style={{ cursor: 'pointer' }} onClick={() => toggleExpand(svc._id)}>
                    <TableCell style={{ padding: '4px 8px' }}>
                      <IconButton size="small">
                        {expanded[svc._id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ fontSize: '0.85rem', fontWeight: 500 }}>{svc.serviceName}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem' }}>{svc.totalQty || '—'}</TableCell>
                    <TableCell style={{ fontSize: '0.8rem', color: '#888' }}>{svc.unit}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem' }}>{fmt(svc.laborCost)}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem' }}>{fmt(svc.materialCost)}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem' }}>{fmt(svc.equipCost)}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem' }}>{fmt(svc.subCost)}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{fmt(svc.totalCost)}</TableCell>
                    <TableCell align="right" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--df-primary, #1f5e3a)' }}>{fmt(svc.totalSell)}</TableCell>
                  </TableRow>

                  {/* Expanded component detail */}
                  {expanded[svc._id] && svc.components.map((c, i) => (
                    <TableRow key={i} style={{ backgroundColor: '#fafafa' }}>
                      <TableCell />
                      <TableCell style={{ paddingLeft: 36, fontSize: '0.78rem' }}>
                        <span style={{ color: COST_TYPE_COLORS[c.costType] || '#666', fontWeight: 500 }}>
                          [{c.costType}]
                        </span>
                        {' '}{c.description || '—'}
                        {c.role && <span style={{ color: '#999', marginLeft: 8 }}>({c.role})</span>}
                      </TableCell>
                      <TableCell align="right" style={{ fontSize: '0.78rem', color: '#888' }}>{c.qty || '—'}</TableCell>
                      <TableCell style={{ fontSize: '0.78rem', color: '#888' }}>
                        {c.hours != null ? `${c.hours.toFixed(1)}h` : ''}
                      </TableCell>
                      <TableCell colSpan={4} />
                      <TableCell align="right" style={{ fontSize: '0.78rem' }}>{fmt(c.extendedCost)}</TableCell>
                      <TableCell align="right" style={{ fontSize: '0.78rem', color: 'var(--df-primary, #1f5e3a)' }}>{fmt(c.sellPrice)}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}

              {/* Category subtotal */}
              <TableRow>
                <TableCell colSpan={4} style={subtotalStyle}>{cat.category} Subtotal</TableCell>
                <TableCell align="right" style={subtotalStyle}>{fmt(cat.subtotals.labor)}</TableCell>
                <TableCell align="right" style={subtotalStyle}>{fmt(cat.subtotals.material)}</TableCell>
                <TableCell align="right" style={subtotalStyle}>{fmt(cat.subtotals.equipment)}</TableCell>
                <TableCell align="right" style={subtotalStyle}>{fmt(cat.subtotals.subcontract)}</TableCell>
                <TableCell align="right" style={subtotalStyle}>{fmt(cat.subtotals.totalCost)}</TableCell>
                <TableCell align="right" style={{ ...subtotalStyle, color: 'var(--df-primary, #1f5e3a)' }}>{fmt(cat.subtotals.totalSell)}</TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {/* Grand total */}
          <TableRow>
            <TableCell colSpan={8} style={{ fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderTop: '2px solid var(--df-primary, #1f5e3a)' }}>
              GRAND TOTAL
            </TableCell>
            <TableCell align="right" style={{ fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderTop: '2px solid var(--df-primary, #1f5e3a)' }}>
              {fmt(data.grandTotal?.totalCost)}
            </TableCell>
            <TableCell align="right" style={{ fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderTop: '2px solid var(--df-primary, #1f5e3a)', color: 'var(--df-primary, #1f5e3a)' }}>
              {fmt(data.grandTotal?.totalSell)}
            </TableCell>
          </TableRow>
          {data.grandTotal && (
            <TableRow>
              <TableCell colSpan={8} style={{ fontSize: '0.8rem', color: '#888', padding: '6px 12px' }}>
                Margin: {((data.grandTotal.margin || 0) * 100).toFixed(1)}% · Markup: {(data.grandTotal.markupPct || 0).toFixed(1)}%
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default ServiceOutputTable
